/* eslint-disable react-hooks/exhaustive-deps */
import { webticsApi } from "@anna-money/webtics";
import { type FC, useEffect, useRef } from "react";
import { hotjar } from "react-hotjar";
import { useLocation } from "react-router-dom";
import { useServices } from "appServices";

export const Hotjar: FC = () => {
  const { userStore, analyticsManager, config } = useServices();
  const sessionId = webticsApi()?.sessionId;
  const location = useLocation();
  const initializedRef = useRef(false);

  const identify = (reason: string): void => {
    if (!hotjar.initialized()) {
      return;
    }

    const payload = {
      alias: userStore.user?.alias,
      session_id: sessionId,
      region: config.region,
      reason,
    };

    try {
      hotjar.identify(userStore.user?.alias || null, payload);
      hotjar.stateChange(location.pathname);
      analyticsManager.event("hotjar-identify", payload);
    } catch (error: unknown) {
      analyticsManager.event("hotjar-identify-failed", {
        error: (error as Error).message,
        region: config.region,
        alias: userStore.user?.alias,
        reason,
      });
    }
  };

  // Initialize Hotjar ONCE per tab load
  useEffect(() => {
    if (initializedRef.current) {
      return;
    }

    hotjar.initialize(3520718, 6);
    initializedRef.current = true;

    const retry = setInterval(() => {
      if (hotjar.initialized()) {
        identify("initial-load");
        clearInterval(retry);
      }
    }, 50);

    return () => {
      clearInterval(retry);
    };
  }, []);

  // SPA route change
  useEffect(() => {
    if (hotjar.initialized()) {
      hotjar.stateChange(location.pathname);
      analyticsManager.event("hotjar-statechange", {
        pathname: location.pathname,
        region: config.region,
      });
    }
  }, [location.pathname]);

  // Resume after idle / tab switch
  useEffect(() => {
    const onResume = (): void => {
      identify("resume");
    };

    const onVisibilityChange = (): void => {
      if (document.visibilityState === "visible") {
        onResume();
      }
    };

    window.addEventListener("focus", onResume);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.removeEventListener("focus", onResume);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [userStore.user?.alias, sessionId, location.pathname]);

  return null;
};
